import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API, fileUrl } from "../lib/api";
import { Check, LogIn, LogOut, Loader2, Calendar, Skull, X, Search, LayoutGrid, List, User } from "lucide-react";

const fmtDay = (d) => new Date(d).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
const fmtShort = (d) => new Date(d).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });

const Screen = ({ children }) => (
  <div className="min-h-screen bg-[#050505] text-[#F2EBE1] p-6">{children}</div>
);

const Loading = () => (
  <div className="min-h-screen bg-[#050505] flex items-center justify-center"><Loader2 className="animate-spin text-blood" size={32} /></div>
);

// --- Date picker (also used inside the switch modal) ----------------------
const DateList = ({ event, dates, onPick, testidPrefix, hideHeader }) => (
  <div className="max-w-3xl mx-auto">
    {!hideHeader && (
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-3 mb-3">
          <Skull size={26} className="text-blood" strokeWidth={1.5} />
          <span className="font-display text-2xl font-bold tracking-[0.04em] text-blood">SCARE AUDITIONS<span className="text-[0.45em] font-semibold tracking-normal align-top opacity-80 ml-1">.co.uk</span></span>
        </div>
        <h1 className="font-display text-4xl font-black uppercase tracking-tighter">{event?.name || "Sign In"}</h1>
        <p className="text-[#A19D94] mt-2 text-lg">Select tonight's date to open the sign-in board</p>
      </div>
    )}
    <div className="grid sm:grid-cols-2 gap-3">
      {dates.length === 0 && <p className="col-span-2 text-center text-[#6B6862] py-12">No open dates have been set up yet.</p>}
      {dates.map((d) => (
        <button key={d.id} onClick={() => onPick(d.id)} data-testid={`${testidPrefix}-${d.id}`}
          className="p-5 border-2 border-[#262626] hover:border-blood active:scale-[0.98] transition-all text-left flex items-center gap-4">
          <Calendar size={26} className="text-blood shrink-0" />
          <div className="min-w-0">
            <div className="font-display text-xl uppercase truncate">{d.name}</div>
            <div className="text-sm text-[#A19D94] font-mono">{fmtShort(d.date)}</div>
          </div>
        </button>
      ))}
    </div>
  </div>
);

export default function TabletSignIn() {
  const { showDateId: paramId } = useParams();
  const [event, setEvent] = useState(null);
  const [dates, setDates] = useState(null);
  const [selected, setSelected] = useState(paramId || null);
  const [data, setData] = useState(null);            // roster for the selected date
  const [pending, setPending] = useState({});
  const [showSwitch, setShowSwitch] = useState(false);
  const [view, setView] = useState("card");          // Update 27 Change 04: card | list
  const [q, setQ] = useState("");                     // search by name / role
  const tap = useRef({ count: 0, timer: null });

  // Load the list of open dates (for picker + switch modal)
  useEffect(() => {
    axios.get(`${API}/public/signin-dates`).then(({ data }) => { setEvent(data.event); setDates(data.dates || []); });
  }, []);

  // Load + auto-refresh the roster for the selected date
  useEffect(() => {
    if (!selected) { setData(null); return; }
    let active = true;
    const load = () => axios.get(`${API}/public/signin/${selected}`).then(({ data }) => active && setData(data)).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => { active = false; clearInterval(t); };
  }, [selected]);

  const refresh = () => selected && axios.get(`${API}/public/signin/${selected}`).then(({ data }) => setData(data));

  const doAction = async (actorId, action) => {
    setPending((p) => ({ ...p, [actorId]: true }));
    try {
      await axios.post(`${API}/public/signin`, { applicant_id: actorId, show_date_id: selected, action });
      await refresh();
    } finally { setPending((p) => ({ ...p, [actorId]: false })); }
  };

  // Hidden 5-tap corner button → opens the date-switch modal (admins only)
  const secretTap = () => {
    const s = tap.current;
    s.count += 1;
    clearTimeout(s.timer);
    s.timer = setTimeout(() => { s.count = 0; }, 1500);
    if (s.count >= 5) { s.count = 0; setShowSwitch(true); }
  };

  if (dates === null) return <Loading />;

  // No date chosen yet → show the picker
  if (!selected) {
    return <Screen><DateList event={event} dates={dates} onPick={setSelected} testidPrefix="signin-date" /></Screen>;
  }

  if (!data) return <Loading />;

  return (
    <Screen>
      {/* Invisible 5-tap corner to switch dates */}
      <button onClick={secretTap} data-testid="signin-secret-corner" aria-hidden
        className="fixed top-0 right-0 w-20 h-20 z-40 opacity-0" />

      <header className="text-center mb-6 pb-6 border-b border-[#1a1a1a]">
        <div className="label-mini">{fmtDay(data.show_date.date)}</div>
        <h1 className="font-display text-5xl font-black uppercase tracking-tighter mt-2">{data.show_date.name}</h1>
        <p className="text-[#A19D94] mt-2 text-lg">Tap a name to sign in / out</p>
      </header>

      {(() => {
        const s = q.trim().toLowerCase();
        const filtered = !s ? data.roster : data.roster.filter((r) =>
          (r.actor_name || "").toLowerCase().includes(s) || (r.role_name || "").toLowerCase().includes(s));
        return (
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-5">
              <div className="relative flex-1">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6862]" />
                <input data-testid="signin-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or role…"
                  className="w-full bg-[#0A0A0A] border-2 border-[#262626] pl-11 pr-4 py-3 text-base focus:outline-none focus:border-blood" />
              </div>
              <div className="flex border-2 border-[#262626] shrink-0">
                <button onClick={() => setView("card")} data-testid="signin-view-card" title="Card view"
                  className={`p-3 transition-colors ${view === "card" ? "bg-blood text-white" : "text-[#A19D94] hover:text-blood"}`}><LayoutGrid size={18} /></button>
                <button onClick={() => setView("list")} data-testid="signin-view-list" title="List view"
                  className={`p-3 transition-colors ${view === "list" ? "bg-blood text-white" : "text-[#A19D94] hover:text-blood"}`}><List size={18} /></button>
              </div>
            </div>

            {filtered.length === 0 ? (
              <p className="text-center text-[#6B6862] py-12">{data.roster.length === 0 ? "No actors rostered for this show yet." : "No actors match your search."}</p>
            ) : view === "list" ? (
              <div className="border border-[#1a1a1a] divide-y divide-[#1a1a1a]" data-testid="signin-list">
                {filtered.map((r) => {
                  const signedIn = !!r.signed_in_at;
                  const signedOut = !!r.signed_out_at;
                  const isPending = pending[r.actor_id];
                  return (
                    <div key={r.actor_id} data-testid={`signin-list-row-${r.actor_id}`}
                      className={`flex items-center gap-4 px-4 py-3 ${signedOut ? "bg-[#3B82F6]/10" : signedIn ? "bg-[#F59E0B]/10" : ""}`}>
                      {r.headshot_file_id ? (
                        <img src={fileUrl(r.headshot_file_id, { thumb: true })} alt="" loading="lazy" decoding="async" className="w-12 h-12 object-cover border border-[#262626] shrink-0 bg-[#1a1a1a]" />
                      ) : (
                        <div className="w-12 h-12 bg-[#1a1a1a] border border-[#262626] flex items-center justify-center shrink-0"><User size={20} className="text-[#6B6862]" /></div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-display text-lg uppercase tracking-tight truncate">{r.actor_name}</div>
                        <div className="text-xs text-[#A19D94] font-mono uppercase tracking-wider truncate">{r.role_name}</div>
                      </div>
                      <div className="shrink-0 text-right">
                        {signedOut ? <span className="text-xs font-mono text-[#3B82F6] uppercase">Out {r.signed_out_at?.slice(11,16)}</span>
                          : signedIn ? <span className="text-xs font-mono text-[#F59E0B] uppercase">In {r.signed_in_at?.slice(11,16)}</span>
                          : <span className="text-xs font-mono text-[#6B6862] uppercase">Not arrived</span>}
                      </div>
                      <button onClick={() => doAction(r.actor_id, signedIn && !signedOut ? "sign_out" : "sign_in")}
                        disabled={isPending || signedOut} data-testid={`signin-list-btn-${r.actor_id}`}
                        className={`shrink-0 px-5 py-2.5 text-sm uppercase tracking-wider font-medium border-2 flex items-center gap-2 transition-all ${
                          signedOut ? "border-[#3B82F6] text-[#3B82F6] opacity-60" :
                          signedIn ? "border-[#F59E0B] text-[#F59E0B] hover:bg-[#F59E0B]/10" :
                          "border-blood text-white bg-blood hover:bg-[#FF3333]"} disabled:opacity-60`}>
                        {isPending ? <Loader2 className="animate-spin" size={16} /> :
                         signedOut ? <><Check size={16} /> Done</> :
                         signedIn ? <><LogOut size={16} /> Sign Out</> :
                         <><LogIn size={16} /> Sign In</>}
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                {filtered.map((r) => {
                  const signedIn = !!r.signed_in_at;
                  const signedOut = !!r.signed_out_at;
                  const isPending = pending[r.actor_id];
                  return (
                    <button key={r.actor_id}
                      onClick={() => doAction(r.actor_id, signedIn && !signedOut ? "sign_out" : "sign_in")}
                      disabled={isPending || signedOut}
                      data-testid={`signin-${r.actor_id}`}
                      className={`p-6 border-2 text-left transition-all min-h-[100px] ${
                        signedOut ? "border-[#3B82F6] bg-[#3B82F6]/10" :
                        signedIn ? "border-[#F59E0B] bg-[#F59E0B]/10" :
                        "border-[#262626] hover:border-blood active:scale-[0.98]"
                      } disabled:opacity-60`}>
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="font-display text-2xl uppercase truncate">{r.actor_name}</div>
                          <div className="text-xs text-[#A19D94] mt-1 font-mono uppercase tracking-wider">{r.role_name}</div>
                          {signedIn && <div className="text-xs text-[#F59E0B] mt-2 font-mono">IN: {r.signed_in_at?.slice(11,16)}</div>}
                          {signedOut && <div className="text-xs text-[#3B82F6] font-mono">OUT: {r.signed_out_at?.slice(11,16)}</div>}
                        </div>
                        <div className="shrink-0">
                          {isPending ? <Loader2 className="animate-spin text-blood" size={28} /> :
                           signedOut ? <Check className="text-[#3B82F6]" size={32} /> :
                           signedIn ? <LogOut className="text-[#F59E0B]" size={32} /> :
                           <LogIn className="text-blood" size={32} />}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      {/* Date-switch modal (revealed by the 5-tap corner) */}
      {showSwitch && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6" data-testid="signin-switch-modal" onClick={() => setShowSwitch(false)}>
          <div className="bg-[#0A0A0A] border border-[#262626] w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-2xl uppercase tracking-tight">Switch Date</h2>
              <button onClick={() => setShowSwitch(false)} data-testid="signin-switch-close" className="text-[#6B6862] hover:text-[#F2EBE1]"><X size={22} /></button>
            </div>
            <DateList event={event} dates={dates} hideHeader
              onPick={(id) => { setSelected(id); setShowSwitch(false); }} testidPrefix="signin-switch-date" />
          </div>
        </div>
      )}
    </Screen>
  );
}
